import React, { Component } from 'react';
import { convertToMoney, formatEventDateTime } from '../utils/formats';
import { formatCounter, includedOriginText } from '../utils/eventIncluded';

const money = (value) => `$ ${convertToMoney(Number(value) || 0)}`;

// Unidades entregadas sin costo cargado (`unitsWithoutCost` del Resultado). Un backend que
// todavía no lo informa no manda la clave: se lee como 0 y la card queda como antes.
const withoutCost = (row) => Math.max(0, Number(row && row.unitsWithoutCost) || 0);
const unidades = (n) => (n === 1 ? '1 unidad' : `${n} unidades`);

// Un ítem de afuera llega sin nombre (`name: null`) si se borró del todo después de servirlo. Se
// dice que ya no está en el menú, NUNCA por su código: un número de trece cifras no le dice a
// nadie qué se sirvió.
const outsideName = (o) => o.name || 'un ítem que ya no está en el menú';

// La celda de costo de un renglón. El costo que llega es el de las unidades que SÍ lo tienen:
// si a alguna le falta, se dice al lado en vez de dejar leer un costo más bajo que el real.
const costCell = (row) => {
    const missing = withoutCost(row);
    return (
        <td className="text-right">
            {money(row.cost)}
            {missing > 0 && <small className="d-block text-warning event-included-cost-missing">{unidades(missing)} sin costo</small>}
        </td>
    );
};

// Lo incluido en la entrada de un evento (spec-eventos-consumo-incluido FR-101 a FR-104): antes
// del evento, cuánto hay que tener ("Para 20 personas: 10 Pizza · 20 Bebida"); durante, cuánto se
// entregó de cada cosa; y siempre, el Resultado del evento con la plata.
//
// No calcula nada: dibuja `event.included`, que arma el backend en cada lectura. Tres casos:
//   · el evento no declara qué incluye (la clave no viaja) → no se dibuja nada;
//   · `included: null` → hay inclusiones pero el cálculo falló; se dice, y el resto sigue;
//   · un objeto → el resumen y el Resultado.
class EventIncludedCard extends Component {
    constructor(props) {
        super(props);
        this.state = { showOrders: false };
    }

    renderCounter(included, anyDelivered) {
        const rules = included.rules || [];
        const outside = included.outside || [];
        const notes = included.notes || [];
        const people = Number(included.people) || 0;
        const personas = `Para ${people} ${people === 1 ? 'persona' : 'personas'}`;

        if (!anyDelivered && people === 0) {
            // Todavía no se vendió ninguna entrada: "Para 0 personas: 0 Pizza" no dice nada.
            // Se muestra qué incluye, y que las cantidades salen de las entradas vendidas.
            return (
                <div className="mb-0 event-included-pending">
                    <p className="mb-1"><strong>Se calcula con las entradas vendidas.</strong></p>
                    {rules.length > 0 && <p className="mb-1">Incluye: {rules.map((rule) => rule.label).join(' · ')}</p>}
                    {rules.filter((rule) => rule.stale).map((rule) => (
                        <small key={rule.label} className="text-warning d-block">
                            {rule.label}: a revisar — alguno de sus ítems ya no está en el menú.
                        </small>
                    ))}
                    {notes.length > 0 && <p className="mb-0 text-muted">También incluye: {notes.join(' · ')}</p>}
                </div>
            );
        }

        if (!anyDelivered) {
            // Todavía no se entregó nada: lo que importa es cuánto hay que tener (FR-101).
            const pactado = rules.map((rule) => `${Number(rule.allowance) || 0} ${rule.label}`).join(' · ');
            return (
                <div className="mb-3">
                    <p className="mb-1 event-included-allowance">
                        <strong>{personas}{pactado !== '' ? ':' : ''}</strong> {pactado}
                    </p>
                    {rules.filter((rule) => rule.stale).map((rule) => (
                        <small key={rule.label} className="text-warning d-block">
                            {rule.label}: a revisar — alguno de sus ítems ya no está en el menú.
                        </small>
                    ))}
                    {notes.length > 0 && <p className="mb-0 text-muted">También incluye: {notes.join(' · ')}</p>}
                </div>
            );
        }

        return (
            <div className="mb-3">
                <p className="mb-1"><strong>{personas}.</strong> Entregado de lo incluido:</p>
                <p className="mb-1 event-included-counter">
                    {formatCounter(included).map((row, i) => (
                        <span key={i} className={row.over ? 'text-danger font-weight-bold mr-3' : 'mr-3'}>
                            {row.text}{row.over ? ' (excedido)' : ''}{row.stale ? ' · a revisar' : ''}
                        </span>
                    ))}
                </p>
                {outside.length > 0 && (
                    <p className="mb-1 text-danger event-included-outside">
                        Fuera de lo pactado: {outside.map((o) => `${o.quantity} × ${outsideName(o)}`).join(' · ')}
                    </p>
                )}
                {notes.length > 0 && <p className="mb-0 text-muted">También incluye: {notes.join(' · ')}</p>}
            </div>
        );
    }

    renderOrders(orders) {
        return (
            <table className="table table-sm mb-0 event-included-orders">
                <thead className="bg-light">
                    <tr>
                        <th>Hora</th>
                        <th>Mesa</th>
                        <th>Origen</th>
                        <th>Ítems</th>
                        <th className="text-right">Valor de lista</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map((order) => (
                        <tr key={order.purchaseId}>
                            {/* Texto en hora del local: se leen los dígitos, sin pasar por Date. */}
                            <td className="text-nowrap">{formatEventDateTime(order.date)}</td>
                            <td>{order.tableLabel || '—'}</td>
                            {/* Las mismas frases que la cuenta de la mesa. */}
                            <td>{includedOriginText(order) || '—'}</td>
                            <td>{(order.items || []).map((item) => `${item.quantity} × ${item.name}`).join(' · ')}</td>
                            <td className="text-right text-nowrap">{money(order.listValue)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    renderResult(included) {
        const rules = included.rules || [];
        const outside = included.outside || [];
        const orders = included.orders || [];
        const tickets = included.tickets || {};
        const extras = included.extras || {};
        const totals = included.totals || {};
        return (
            <div className="event-included-result">
                <h6 className="text-uppercase text-muted">Resultado del evento</h6>
                <p className="mb-2">
                    Entradas: <strong>{money(tickets.collected)}</strong> recaudado · {money(tickets.pending)} a cobrar
                </p>
                <table className="table table-sm mb-2">
                    <thead className="bg-light">
                        <tr>
                            <th>Incluido</th>
                            <th className="text-right">Entregado</th>
                            <th className="text-right">Pactado</th>
                            <th className="text-right">Valor de lista</th>
                            <th className="text-right">Costo</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rules.map((rule, i) => {
                            const over = Number(rule.delivered) > Number(rule.allowance);
                            return (
                                <tr key={`r-${i}`} className={over ? 'text-danger' : undefined}>
                                    <td>{rule.label}{over ? ' (excedido)' : ''}{rule.stale ? ' · a revisar' : ''}</td>
                                    <td className="text-right">{rule.delivered}</td>
                                    <td className="text-right">{rule.allowance}</td>
                                    <td className="text-right">{money(rule.listValue)}</td>
                                    {costCell(rule)}
                                </tr>
                            );
                        })}
                        {outside.map((o) => (
                            <tr key={`o-${o.code}`} className="text-danger">
                                <td>Fuera de lo pactado: {outsideName(o)}</td>
                                <td className="text-right">{o.quantity}</td>
                                <td className="text-right">—</td>
                                <td className="text-right">{money(o.listValue)}</td>
                                {costCell(o)}
                            </tr>
                        ))}
                        <tr className="font-weight-bold">
                            <td colSpan="3">Total marcado como incluido</td>
                            <td className="text-right">{money(totals.listValue)}</td>
                            <td className="text-right">{money(totals.cost)}</td>
                        </tr>
                    </tbody>
                </table>
                {withoutCost(totals) > 0 && (
                    <p className="mb-2 text-warning event-included-cost-incomplete">
                        Costo incompleto: {unidades(withoutCost(totals))} sin costo cargado. El costo real es mayor que el que se muestra.
                    </p>
                )}
                <p className="mb-2">
                    Extras de las mesas (comandas que se cobran): <strong>{money(extras.paid)}</strong> cobrados · {money(extras.pending)} pendientes
                </p>
                {orders.length === 0 ? (
                    <small className="text-muted">Todavía no hay comandas incluidas.</small>
                ) : (
                    <>
                        <button type="button" className="btn btn-sm btn-white" onClick={() => this.setState({ showOrders: !this.state.showOrders })}>
                            {this.state.showOrders ? 'Ocultar las comandas incluidas' : `Ver las comandas incluidas (${orders.length})`}
                        </button>
                        {orders.length >= 200 && (
                            <small className="text-muted ml-2">Se listan las 200 más recientes; los totales cuentan todas.</small>
                        )}
                        {this.state.showOrders && <div className="mt-2 table-responsive">{this.renderOrders(orders)}</div>}
                    </>
                )}
            </div>
        );
    }

    render() {
        const event = this.props.event;
        if (!event) return null;
        const included = event.included;
        const declara = (event.ticketTypes || []).some((tt) => (tt.inclusions || []).length > 0);
        // Sin la clave y sin inclusiones: el evento no usa esto y la pantalla queda como siempre.
        if (included === undefined && !declara) return null;

        if (included == null || typeof included !== 'object') {
            return (
                <div className="card card-small mb-4">
                    <div className="card-header border-bottom">
                        <h6 className="m-0">Incluido en la entrada</h6>
                    </div>
                    <div className="card-body">
                        <p className="mb-0 text-muted">
                            No se pudo calcular lo incluido. El resto del evento funciona igual; recargá la página para reintentar.
                        </p>
                    </div>
                </div>
            );
        }

        const anyDelivered = (included.rules || []).some((rule) => Number(rule.delivered) > 0)
            || (included.outside || []).length > 0
            || (included.orders || []).length > 0;
        const tickets = included.tickets || {};
        const extras = included.extras || {};
        const anyAdmissions = Number(included.people) > 0 || Number(included.unpaidAdmissions) > 0
            || Number(tickets.collected) > 0 || Number(tickets.pending) > 0;
        const anyOrders = anyDelivered || Number(extras.paid) > 0 || Number(extras.pending) > 0;
        return (
            <div className="card card-small mb-4">
                <div className="card-header border-bottom">
                    <h6 className="m-0">Incluido en la entrada</h6>
                </div>
                <div className="card-body">
                    {Number(included.unpaidAdmissions) > 0 && (
                        <div className="alert alert-warning py-2">
                            La entrada del evento está impaga: {money(included.unpaidAmount)} a cobrar en Eventos → Anotados
                        </div>
                    )}
                    {this.renderCounter(included, anyDelivered)}
                    {/* El Resultado aparece cuando hay algo que resultar: entradas o comandas. */}
                    {(anyAdmissions || anyOrders) && this.renderResult(included)}
                </div>
            </div>
        );
    }
}

export default EventIncludedCard;
